"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { HocTapLoginModal } from "@/components/auth/hoc-tap-login-modal";
import { getApiUrl } from "@/lib/api";
import { showToast } from "@/components/ui/modal-toast-provider";

export type SiteHeaderItem = {
  href: string;
  label: string;
  isAction?: boolean;
  target?: string;
  rel?: string;
};

const RULES_URL =
  "https://docs.google.com/document/d/1J1FeHdhzccuAUHWKeQHceVByTcYplq2aBU82eSwDKKo/edit?usp=sharing";

const RULES_NAV_ITEM: SiteHeaderItem = {
  href: RULES_URL,
  label: "Thể Lệ",
  isAction: false,
  target: "_blank",
  rel: "noopener noreferrer",
};

type SiteHeaderProps = {
  items?: readonly SiteHeaderItem[];
};

export function SiteHeader({ items }: SiteHeaderProps) {
  const router = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMemberLoggedIn, setIsMemberLoggedIn] = useState(false);
  const [isGroupLoggedIn, setIsGroupLoggedIn] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const closeMenu = () => setIsMenuOpen(false);

  const handleLogout = async () => {
    const memberToken = localStorage.getItem("member_token");
    const groupToken = localStorage.getItem("token");
    const apiUrl = getApiUrl();

    if (memberToken) {
      try {
        await fetch(`${apiUrl}/api/v1/member/auth/logout`, {
          method: "POST",
          credentials: "include",
          headers: { Authorization: `Bearer ${memberToken}` },
        });
      } catch (err) {
        console.warn("Member logout error:", err);
      }
    }

    if (groupToken) {
      try {
        await fetch(`${apiUrl}/api/v1/group/auth/logout`, {
          method: "POST",
          credentials: "include",
          headers: { Authorization: `Bearer ${groupToken}` },
        });
      } catch (err) {
        console.warn("Group logout error:", err);
      }
    }

    localStorage.removeItem("member_token");
    localStorage.removeItem("member_user");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsMemberLoggedIn(false);
    setIsGroupLoggedIn(false);
    window.dispatchEvent(new Event("auth-change"));
    showToast("Đã đăng xuất thành công", "success");

    if (
      typeof window !== "undefined" &&
      (window.location.pathname.startsWith("/hoc-tap/dashboard") ||
        window.location.pathname.startsWith("/the-le"))
    ) {
      router.push("/");
    }
  };

  useEffect(() => {
    const syncAuth = () => {
      setIsMemberLoggedIn(!!localStorage.getItem("member_token"));
      setIsGroupLoggedIn(!!localStorage.getItem("token"));
    };

    const handleOpenLoginModal = () => setIsLoginModalOpen(true);

    syncAuth();
    window.addEventListener("storage", syncAuth);
    window.addEventListener("auth-change", syncAuth);
    window.addEventListener("open-hoc-tap-login", handleOpenLoginModal);

    const updateHeaderState = () => setIsScrolled(window.scrollY > 16);

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMenuOpen(false);
    };

    updateHeaderState();
    window.addEventListener("scroll", updateHeaderState, { passive: true });
    document.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("storage", syncAuth);
      window.removeEventListener("auth-change", syncAuth);
      window.removeEventListener("open-hoc-tap-login", handleOpenLoginModal);
      window.removeEventListener("scroll", updateHeaderState);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  // Calculate navigation items based on user requirements:
  // Menu bao gồm: Cổng dự thi, Cổng đào tạo, Thể Lệ, Đăng nhập (hoặc Tài khoản), Đăng xuất
  let navItems: SiteHeaderItem[];

  if (items) {
    navItems = [...items];
  } else if (isMemberLoggedIn) {
    // 3: Khi login với tk học (member_token)
    // - Cổng đào tạo -> trang kết nối hoola (/hoc-tap/dashboard)
    // - Đăng nhập -> đổi tên thành "Tài khoản" và bỏ background (isAction: false)
    // - Cổng dự thi -> mở ra trang login dự thi (/login)
    // - Thể Lệ -> Google Docs thể lệ cuộc thi
    // - Đăng xuất -> đăng xuất tài khoản
    navItems = [
      { href: "/login", label: "Cổng dự thi", isAction: false },
      { href: "/hoc-tap/hoola-sso", label: "Cổng đào tạo", isAction: false },
      RULES_NAV_ITEM,
      { href: "/hoc-tap/dashboard", label: "Tài khoản", isAction: false },
      { href: "#logout", label: "Đăng xuất", isAction: true },
    ];
  } else if (isGroupLoggedIn) {
    // 4: Khi login với tk dự thi (token)
    // - Cổng dự thi -> trang chọn lựa phần thi (/the-le)
    // - Cổng đào tạo, đăng nhập -> trang đăng nhập học (/hoc-tap/login)
    // - Thể Lệ -> Google Docs thể lệ cuộc thi
    // - Đăng xuất -> đăng xuất tài khoản dự thi
    navItems = [
      { href: "/the-le", label: "Cổng dự thi", isAction: false },
      { href: "/hoc-tap/login", label: "Cổng đào tạo", isAction: false },
      RULES_NAV_ITEM,
      { href: "#logout", label: "Đăng xuất", isAction: true },
    ];
  } else {
    // 2: Khi chưa login
    // - Cổng dự thi -> trang login dự thi (/login)
    // - Cổng đào tạo, đăng nhập -> trang đăng nhập học (/hoc-tap/login)
    // - Thể Lệ -> Google Docs thể lệ cuộc thi
    navItems = [
      { href: "/login", label: "Cổng dự thi", isAction: false },
      { href: "/hoc-tap/login", label: "Cổng đào tạo", isAction: false },
      RULES_NAV_ITEM,
      { href: "/hoc-tap/login", label: "Đăng nhập", isAction: true },
    ];
  }

  return (
    <>
      <header
        className={[
          "site-header",
          isScrolled ? "active" : "",
          isMenuOpen ? "is-menu-open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label="Điều hướng chính"
      >
        <Link
          className="brand"
          href="/"
          aria-label="Panasonic - Trang chủ"
          onClick={closeMenu}
        >
          <Image
            src="/assets/images/panasonic-logo-white.png"
            alt="Panasonic"
            width={282}
            height={42}
            priority
          />
        </Link>

        <button
          className="menu-toggle"
          type="button"
          aria-expanded={isMenuOpen}
          aria-controls="primary-nav"
          onClick={() => setIsMenuOpen((isOpen) => !isOpen)}
        >
          <span className="menu-icon" aria-hidden="true" />
          <span className="sr-only">
            {isMenuOpen ? "Đóng menu" : "Mở menu"}
          </span>
        </button>

        <nav
          id="primary-nav"
          className={`primary-nav${isMenuOpen ? " is-open" : ""}`}
        >
          {navItems.map((item, index) => {
            const isExternal =
              item.href.startsWith("http://") || item.href.startsWith("https://");
            const target = item.target ?? (isExternal ? "_blank" : undefined);
            const rel =
              item.rel ?? (isExternal ? "noopener noreferrer" : undefined);
            const isHocTapLogin =
              item.href === "/hoc-tap/login" ||
              (item.label === "Cổng đào tạo" && !isMemberLoggedIn);
            const isLogout =
              item.label === "Đăng xuất" || item.href === "#logout";

            return (
              <Link
                className={item.isAction ? "login-button" : undefined}
                href={item.href}
                key={`${item.href}-${item.label}-${index}`}
                onClick={(e) => {
                  if (isHocTapLogin) {
                    e.preventDefault();
                    closeMenu();
                    setIsLoginModalOpen(true);
                    return;
                  }
                  if (isLogout) {
                    e.preventDefault();
                    closeMenu();
                    handleLogout();
                    return;
                  }
                  closeMenu();
                }}
                target={target}
                rel={rel}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
      </header>

      <HocTapLoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />
    </>
  );
}
